import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { Toast as T } from 'radix-ui';
import { type ReactNode, useSyncExternalStore } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import s from './Toast.module.css';

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: 'success' | 'danger' | 'info';
  action?: { label: string; onClick: () => void };
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
  open: boolean;
}

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

/** Show a toast from anywhere: `toast({ title: t('Saved'), tone: 'success' })`. */
export function toast(input: ToastInput): void {
  items = [...items.slice(-3), { ...input, id: ++seq, open: true }];
  emit();
}

function close(id: number): void {
  items = items.map((i) => (i.id === id ? { ...i, open: false } : i));
  emit();
  setTimeout(() => {
    items = items.filter((i) => i.id !== id);
    emit();
  }, 300);
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const ICONS = { success: CircleCheck, danger: CircleAlert, info: Info };

export function Toaster() {
  const list = useSyncExternalStore(
    subscribe,
    () => items,
    () => items,
  );
  return (
    <T.Provider swipeDirection="right" duration={5000}>
      {list.map((item) => {
        const Icon = ICONS[item.tone ?? 'info'];
        return (
          <T.Root
            key={item.id}
            open={item.open}
            onOpenChange={(o) => !o && close(item.id)}
            duration={item.duration}
            className={cx(s.toast, s[item.tone ?? 'info'])}
          >
            <Icon className={s.icon} aria-hidden />
            <div className={s.body}>
              <T.Title className={s.title}>{item.title}</T.Title>
              {item.description && <T.Description className={s.description}>{item.description}</T.Description>}
            </div>
            {item.action && (
              <T.Action altText={item.action.label} asChild>
                <button type="button" className={s.action} onClick={item.action.onClick}>
                  {item.action.label}
                </button>
              </T.Action>
            )}
            <T.Close className={s.close} aria-label={t('Close')}>
              <X aria-hidden />
            </T.Close>
          </T.Root>
        );
      })}
      <T.Viewport className={s.viewport} />
    </T.Provider>
  );
}
